const text = (value) => String(value ?? '').trim();
const key = (value) => text(value).toLocaleLowerCase();

function parseJsonArray(value) {
    if (typeof value !== 'string') return null;
    const raw = value.trim();
    if (!raw.startsWith('[')) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) throw new Error('Expected a JSON array');
    return parsed;
}

export function normalizeThemePairs(value, legacyTheme = '', legacySubtheme = '') {
    let input = value;
    if (typeof input === 'string') {
        const parsed = parseJsonArray(input);
        if (parsed) input = parsed;
        else if (input.trim()) {
            input = input.split(';').map((part) => {
                const slash = part.indexOf('/');
                return slash < 0
                    ? { theme: part, subtheme: '' }
                    : { theme: part.slice(0, slash), subtheme: part.slice(slash + 1) };
            });
        }
    }
    if (!Array.isArray(input) || input.length === 0) {
        input = text(legacyTheme)
            ? [{ theme: legacyTheme, subtheme: legacySubtheme }]
            : [];
    }

    const seen = new Set();
    const out = [];
    for (const item of input) {
        const pair = typeof item === 'string'
            ? { theme: item, subtheme: '' }
            : item;
        const theme = text(pair?.theme);
        const subtheme = text(pair?.subtheme);
        if (!theme) continue;
        const identity = `${key(theme)}\u0000${key(subtheme)}`;
        if (seen.has(identity)) continue;
        seen.add(identity);
        out.push({ theme, subtheme });
    }
    return out;
}

export function normalizeSubtypes(value, legacySubtype = '') {
    let input = value;
    if (typeof input === 'string') {
        const parsed = parseJsonArray(input);
        input = parsed || input.split(/[,;]/);
    }
    if (!Array.isArray(input) || input.length === 0) {
        input = text(legacySubtype) ? [legacySubtype] : [];
    }
    const seen = new Set();
    const out = [];
    for (const item of input) {
        const subtype = text(item);
        const identity = key(subtype);
        if (!subtype || seen.has(identity)) continue;
        seen.add(identity);
        out.push(subtype);
    }
    return out;
}

export function getWordThemePairs(word = {}) {
    return normalizeThemePairs(
        word.theme_pairs ?? word.themePairs,
        word.theme,
        word.subtheme,
    );
}

export function getWordSubtypes(word = {}) {
    return normalizeSubtypes(word.subtypes, word.subtype);
}

export function normalizeWordTaxonomy(word = {}) {
    const themePairs = getWordThemePairs(word);
    const subtypes = getWordSubtypes(word);
    const firstPair = themePairs[0] || { theme: '', subtheme: '' };
    return {
        ...word,
        theme_pairs: themePairs,
        themePairs,
        subtypes,
        theme: firstPair.theme,
        subtheme: firstPair.subtheme,
        subtype: subtypes[0] || '',
    };
}

export function taxonomyPayload(word = {}) {
    const normalized = normalizeWordTaxonomy(word);
    return {
        theme_pairs: normalized.theme_pairs,
        subtypes: normalized.subtypes,
        theme: normalized.theme,
        subtheme: normalized.subtheme,
        subtype: normalized.subtype,
    };
}

export function wordMatchesTheme(word, theme, subtheme = '') {
    const wantedTheme = key(theme);
    const wantedSubtheme = key(subtheme);
    if (!wantedTheme && !wantedSubtheme) return true;
    return getWordThemePairs(word).some((pair) => (
        (!wantedTheme || key(pair.theme) === wantedTheme)
        && (!wantedSubtheme || key(pair.subtheme) === wantedSubtheme)
    ));
}

export function wordHasSubtype(word, subtype) {
    const wanted = key(subtype);
    return !wanted || getWordSubtypes(word).some((value) => key(value) === wanted);
}

export function formatThemePair(pair) {
    const theme = text(pair?.theme);
    const subtheme = text(pair?.subtheme);
    return subtheme ? `${theme} / ${subtheme}` : theme;
}

export function formatThemePairs(wordOrPairs) {
    const pairs = Array.isArray(wordOrPairs)
        ? normalizeThemePairs(wordOrPairs)
        : getWordThemePairs(wordOrPairs);
    return pairs.map(formatThemePair).filter(Boolean).join('; ');
}

export function serializeThemePairsJson(wordOrPairs) {
    const pairs = Array.isArray(wordOrPairs)
        ? normalizeThemePairs(wordOrPairs)
        : getWordThemePairs(wordOrPairs);
    return JSON.stringify(pairs);
}

export function parseThemePairsImport(value, legacyTheme = '', legacySubtheme = '') {
    try {
        return normalizeThemePairs(value, legacyTheme, legacySubtheme);
    } catch (error) {
        throw new Error(`Invalid Theme pairs JSON: ${error.message}`);
    }
}

export function unionThemePairs(...values) {
    return normalizeThemePairs(values.flatMap((value) => normalizeThemePairs(value)));
}

export function unionSubtypes(...values) {
    return normalizeSubtypes(values.flatMap((value) => normalizeSubtypes(value)));
}

export function expandWordByThemePair(word) {
    const pairs = getWordThemePairs(word);
    if (!pairs.length) return [{ ...word, taxonomyPair: null }];
    return pairs.map((pair) => ({
        ...word,
        taxonomyPair: pair,
        theme: pair.theme,
        subtheme: pair.subtheme,
    }));
}

/**
 * Build suggestion lists from existing words.
 * Themes are unique (first casing wins). Subthemes are keyed by theme (case-insensitive).
 * Custom themes/subthemes remain allowed — this is only for suggestions.
 */
export function buildThemeCatalog(words = []) {
    const themeDisplay = new Map(); // themeKey -> label
    const subByTheme = new Map(); // themeKey -> Map(subKey -> label)

    (words || []).forEach((word) => {
        getWordThemePairs(word).forEach((pair) => {
            const themeKey = key(pair.theme);
            if (!themeKey) return;
            if (!themeDisplay.has(themeKey)) themeDisplay.set(themeKey, pair.theme);
            if (!subByTheme.has(themeKey)) subByTheme.set(themeKey, new Map());
            const subKey = key(pair.subtheme);
            if (!subKey) return;
            const subMap = subByTheme.get(themeKey);
            if (!subMap.has(subKey)) subMap.set(subKey, pair.subtheme);
        });
    });

    const themes = [...themeDisplay.values()].sort((a, b) => a.localeCompare(b));
    const subthemesByThemeKey = {};
    for (const [themeKey, subMap] of subByTheme.entries()) {
        subthemesByThemeKey[themeKey] = [...subMap.values()].sort((a, b) => a.localeCompare(b));
    }

    return { themes, subthemesByThemeKey };
}

/** Subthemes already used with this theme (case-insensitive). Empty theme → no suggestions. */
export function subthemesForTheme(catalog, theme) {
    const themeKey = key(theme);
    if (!catalog || !themeKey) return [];
    return catalog.subthemesByThemeKey?.[themeKey] || [];
}

export function applyTaxonomyOperation(word, operation = 'replace', taxonomy = {}) {
    const hasPairs = Object.prototype.hasOwnProperty.call(taxonomy, 'theme_pairs')
        || Object.prototype.hasOwnProperty.call(taxonomy, 'themePairs');
    const hasSubtypes = Object.prototype.hasOwnProperty.call(taxonomy, 'subtypes');
    const hasType = Object.prototype.hasOwnProperty.call(taxonomy, 'type');

    let themePairs = getWordThemePairs(word);
    let subtypes = getWordSubtypes(word);
    let type = text(word.type);

    if (hasPairs) {
        const incomingPairs = normalizeThemePairs(taxonomy.theme_pairs ?? taxonomy.themePairs);
        if (operation === 'add') {
            themePairs = unionThemePairs(themePairs, incomingPairs);
        } else if (operation === 'remove') {
            const pairKeys = new Set(incomingPairs.map((pair) => `${key(pair.theme)}\u0000${key(pair.subtheme)}`));
            themePairs = themePairs.filter((pair) => !pairKeys.has(`${key(pair.theme)}\u0000${key(pair.subtheme)}`));
        } else {
            themePairs = incomingPairs;
        }
    }

    if (hasSubtypes) {
        const incomingSubtypes = normalizeSubtypes(taxonomy.subtypes);
        if (operation === 'add') {
            subtypes = unionSubtypes(subtypes, incomingSubtypes);
        } else if (operation === 'remove') {
            const subtypeKeys = new Set(incomingSubtypes.map(key));
            subtypes = subtypes.filter((subtype) => !subtypeKeys.has(key(subtype)));
        } else {
            subtypes = incomingSubtypes;
        }
    }

    if (hasType) {
        const incomingType = text(taxonomy.type);
        if (operation === 'remove') {
            if (incomingType && key(incomingType) === key(type)) type = '';
        } else if (operation === 'add') {
            if (incomingType) type = incomingType;
        } else {
            type = incomingType;
        }
    }

    return normalizeWordTaxonomy({ ...word, theme_pairs: themePairs, subtypes, type });
}
